/**
 * Web entry point.
 *
 * Constraints that shape this whole package, all of them requirements:
 *
 *  PC-F06   Every page renders inside ONE consistent frame — same header, navigation,
 *           search field and footer. No surface looks like a different product.
 *  PC-F06a  A search field at the top of EVERY page, same position, visible without
 *           scrolling, never reduced to an icon alone (DS-F16).
 *  PC-F07   Usable embedded in the collaboration workspace: NO popup, NO new window,
 *           NO redirect in any journey. See plan §23-B4 for the one open reading.
 *  PC-F08   A direct link to any tool, thread or profile resolves after silent sign-in.
 *  PC-F09   Where a supplier's data is unavailable, render WITHOUT that element and say
 *           what is missing, rather than failing.
 *  PC-F14   A named future capability appears NOWHERE — no disabled button, no
 *           "coming soon", no nav entry.
 *  PC-NFR01 WCAG 2.1 AA on every surface.
 *  PC-NFR04 First meaningful render under 2 s.
 *
 * The frame, its routes and its styling live in App.tsx / router.tsx. This file only mounts
 * the data router at #root — the browser router (PC-F08: direct links resolve) inside
 * StrictMode.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router/dom';

// The design system — Tailwind's Preflight, the design tokens at `:root`, and the base element
// defaults. Imported HERE, at the root, so it loads once for every surface rather than appearing and
// disappearing with the upload overlay (it used to be imported lazily by UploadOverlayReskin, which
// made the global reset come and go with a modal). See design-system.css for the V1/V2 consequence.
import './design-system.css';

// The V1 page chrome (banner / header / nav / footer and the header's own controls). Imported
// SECOND, so the .tb-* rules keep the cascade position the old <style> tag had. It used to be a
// template literal rendered from inside App, which left the frame unstyled until React mounted —
// a visible flash of full-bleed default-flow content on every cold load. See frame.css's header.
import './frame.css';

// The Community Hub's page layout (the full-bleed navy band + section grids, `.cm-*`). Imported HERE
// for the SAME reason as frame.css above, not inside community/Community.tsx: a component-level CSS
// import is code-split into the route's JS chunk, so on a cold load / hard reload the band's markup
// paints one frame BEFORE its stylesheet applies — the navy band flashes in default document flow
// (at the foot of the page, unstyled) before snapping under the header. Loading it at the root puts
// the rules in the <head> stylesheet ahead of first paint, in dev and prod alike.
import './communityPage.css';

import { router } from './router';

const root = document.getElementById('root');
if (!root) throw new Error('#root missing from index.html');

createRoot(root).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
